import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    ListChecks, CircleCheck, BarChart3, Flame, LineChart, CalendarRange, Database, ArrowUpRight,
} from 'lucide-react';
import { getAllEntities, type Entity } from '../packages/api-client/entities-api';
import { apiClient, getErrorMessage } from '../packages/api-client';
import { getPortfolio, type PortfolioProject } from '../packages/api-client/pm-api';
import { getMyWork, getReviews, type WorkItem, type ReviewItem, type WorkPage } from '../packages/api-client/work-api';
import { PageHeader, StateView } from '../packages/ui';
import { ProjectCard } from '../components/ProjectCard';
import { RecentCommitsWidget } from '../components/RecentCommitsWidget';
import { OnboardingChecklist } from '../components/onboarding/OnboardingChecklist';
import { EntityIcon } from '../components/taskIcons';
import { usePageTitle } from '../hooks/usePageTitle';
import { varAlpha, hexAlpha } from '../utils/statusColors';
import { formatDueDate, isDueDateOverdue } from '../utils/date';
import './DashboardPage.css';

// ─── Data ────────────────────────────────────────────────────────────────────
// Every number on this page comes from an endpoint that already scopes to the
// viewer. /projects/portfolio is one unpaginated response with per-project
// counts computed server-side (no member x task join); each task belongs to
// exactly one project, so adding its rows is exact. Nothing here adds up a
// paginated list or record task links (a task linked to two records counted
// twice, and records are not the viewer's task set).

// Counts are the endpoints' meta.total; only the first few items are listed.
const TOP = 5;
const loadMyWork = () => getMyWork(1, TOP);
const loadReviews = () => getReviews(1, TOP);

const HEAT_DAYS = 84;
const isoDay = (d: Date) => d.toISOString().split('T')[0];
const loadActivity = async () => {
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - (HEAT_DAYS - 1));
    const { data } = await apiClient.get<{ cells: { date: string; count: number }[] }>('/workload/activity', {
        params: { from: isoDay(from), to: isoDay(to) },
    });
    return data.cells ?? [];
};

type Resource<T> = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: T };

/** One fetch with its own loading/error state and a retry; failures are shown, never swallowed. */
function useResource<T>(load: () => Promise<T>): [Resource<T>, () => void] {
    const [state, setState] = useState<Resource<T>>({ status: 'loading' });
    const [attempt, setAttempt] = useState(0);
    useEffect(() => {
        let cancelled = false;
        setState({ status: 'loading' });
        load().then(
            data => { if (!cancelled) setState({ status: 'ready', data }); },
            err => { if (!cancelled) setState({ status: 'error', message: getErrorMessage(err) }); },
        );
        return () => { cancelled = true; };
    }, [load, attempt]);
    return [state, () => setAttempt(n => n + 1)];
}

// ─── Layout pieces ───────────────────────────────────────────────────────────

/** Concept `.panel`: sentence-case h2 title, optional count badge, optional action link. */
function Panel({ id, title, count, action, children, className = '' }: {
    id: string; title: string; count?: number; action?: React.ReactNode; children: React.ReactNode; className?: string;
}) {
    return (
        <section className={`card dash-panel ${className}`} aria-labelledby={id}>
            <div className="dash-panel-head">
                <h2 id={id} className="dash-panel-title">{title}</h2>
                {count !== undefined && <span className="dash-count" aria-label={`${count} total`}>{count}</span>}
                {action && <span className="dash-panel-action">{action}</span>}
            </div>
            {children}
        </section>
    );
}

/** Renders loading / error+Retry for a resource, or the ready content. */
function Load<T>({ res, retry, what, lines, children }: {
    res: Resource<T>; retry: () => void; what: string; lines?: number; children: (data: T) => React.ReactNode;
}) {
    if (res.status === 'loading') return <StateView state="loading" label={`Loading ${what}`} lines={lines} />;
    if (res.status === 'error') return <StateView state="error" title={`Couldn't load ${what}`} message={res.message} onRetry={retry} />;
    return <>{children(res.data)}</>;
}

function Metric({ to, label, value, sub, tone }: { to: string; label: string; value: string; sub?: string; tone?: 'danger' }) {
    return (
        <Link to={to} className="card dash-metric" aria-label={`${label}: ${value}`}>
            <span className="dash-metric-label">{label}</span>
            <strong className={`dash-metric-value${tone ? ` dash-metric-value--${tone}` : ''}`}>{value}</strong>
            {sub && <span className="dash-metric-sub">{sub}</span>}
        </Link>
    );
}

const PORTFOLIO_VIEW = '/projects?view=portfolio';
const PROJECT_CARDS = 6;
const blockedTasksURL = (projectID: string) => `/projects/${projectID}?tab=Tasks&view=list&status=blocked`;
const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

// ─── Cards ───────────────────────────────────────────────────────────────────

/** Concept metric strip: Visible tasks · Progress · Needs review · Blocked. */
function Summary({ portfolio, reviews }: { portfolio: PortfolioProject[]; reviews: Resource<WorkPage<ReviewItem>> }) {
    const total = portfolio.reduce((s, p) => s + p.total_tasks, 0);
    const done = portfolio.reduce((s, p) => s + p.done_tasks, 0);
    const overdue = portfolio.reduce((s, p) => s + p.overdue_tasks, 0);
    const blocked = portfolio.reduce((s, p) => s + p.blocked_tasks, 0);
    const attention = portfolio.filter(p => p.overdue_tasks > 0 || p.blocked_tasks > 0).length;
    // The review count is its own endpoint's meta.total; never a 0 while it loads or after it fails.
    const reviewValue = reviews.status === 'ready' ? String(reviews.data.meta?.total ?? 0) : reviews.status === 'loading' ? '…' : '—';
    return (
        <div className="dash-metrics">
            <Metric to={PORTFOLIO_VIEW} label="Visible tasks" value={String(total)} sub={`Across ${plural(portfolio.length, 'project')}`} />
            {/* 0 of 0 is not 0% complete: with no visible tasks there is no denominator. */}
            <Metric to={PORTFOLIO_VIEW} label="Progress"
                value={total === 0 ? 'No accessible tasks' : `${Math.round((done / total) * 100)}%`}
                sub={total === 0 ? undefined : `${done} of ${total} tasks done`} />
            <Metric to="/reviews" label="Needs review" value={reviewValue}
                sub={reviews.status === 'error' ? "Couldn't load reviews" : undefined} />
            <Metric to={PORTFOLIO_VIEW} label="Blocked" value={String(blocked)} tone={blocked > 0 ? 'danger' : undefined}
                sub={`${overdue} overdue · ${plural(attention, 'project')} need attention`} />
        </div>
    );
}

/** Concept project grid: the shared ProjectCard, projects needing attention first. */
function ProjectGrid({ portfolio }: { portfolio: PortfolioProject[] }) {
    if (portfolio.length === 0) {
        return <StateView state="empty" title="No projects you can access" action={<Link className="btn btn-secondary btn-md" to="/projects">Go to Projects</Link>} />;
    }
    const risk = (p: PortfolioProject) => p.overdue_tasks + p.blocked_tasks;
    const top = [...portfolio].sort((a, b) => risk(b) - risk(a)).slice(0, PROJECT_CARDS);
    return (
        <div className="pcard-grid" role="list" aria-label="Projects">
            {top.map(p => <div role="listitem" key={p.id} className="dash-card-item"><ProjectCard project={p} stats={p} /></div>)}
        </div>
    );
}

/** Blocked work: per-project blocked (and overdue) counts from the portfolio API.
 *  There is no cross-project blocked-task list endpoint, so rows are projects, each
 *  linking to that project's blocked-task filter. */
function BlockedWork({ portfolio }: { portfolio: PortfolioProject[] }) {
    const risky = portfolio.filter(p => p.overdue_tasks > 0 || p.blocked_tasks > 0)
        .sort((a, b) => (b.overdue_tasks + b.blocked_tasks) - (a.overdue_tasks + a.blocked_tasks));
    if (risky.length === 0) {
        return <p className="dash-ok"><CircleCheck size={16} aria-hidden="true" /> No blocked or overdue tasks in your projects</p>;
    }
    return (
        <ul className="dash-list dash-rows">
            {risky.slice(0, 6).map(p => (
                <li key={p.id} className="dash-task-row">
                    <span className={`dash-status-dot ${p.blocked_tasks > 0 ? 'dash-status-dot--blocked' : 'dash-status-dot--overdue'}`} aria-hidden="true" />
                    <span className="dash-task-copy">
                        <Link to={`/projects/${p.id}`} className="dash-task-title"><EntityIcon icon={p.icon} size={14} /> {p.name}</Link>
                        <span className="dash-task-sub">
                            {p.blocked_tasks > 0 && <Link to={blockedTasksURL(p.id)} className="dash-warn">{p.blocked_tasks} blocked</Link>}
                            {p.blocked_tasks > 0 && p.overdue_tasks > 0 && ' · '}
                            {p.overdue_tasks > 0 && <span className="dash-danger">{p.overdue_tasks} overdue</span>}
                        </span>
                    </span>
                    <span className={`dash-pill ${p.blocked_tasks > 0 ? 'dash-pill--amber' : 'dash-pill--danger'}`}>{p.blocked_tasks > 0 ? 'Blocked' : 'Overdue'}</span>
                </li>
            ))}
        </ul>
    );
}

function Deadlines({ portfolio }: { portfolio: PortfolioProject[] }) {
    const upcoming = portfolio
        .filter(p => p.due_date && p.status !== 'completed')
        .sort((a, b) => (a.due_date as string).localeCompare(b.due_date as string))
        .slice(0, 5);
    if (upcoming.length === 0) return <p className="dash-muted">No open project has a due date.</p>;
    return (
        <ul className="dash-list">
            {upcoming.map(p => {
                // Due today is not past due: compare with the start of today, local time.
                const late = isDueDateOverdue(p.due_date);
                return (
                    <li key={p.id} className="dash-attention">
                        <Link to={`/projects/${p.id}?tab=Planning`} className="dash-row-name">{p.name}</Link>
                        <span className={late ? 'dash-danger' : 'dash-muted'}>{late ? 'Past due · ' : ''}{formatDueDate(p.due_date)}</span>
                    </li>
                );
            })}
        </ul>
    );
}

function AssignedToYou({ work }: { work: WorkPage<WorkItem> }) {
    const total = work.meta?.total ?? 0;
    if (total === 0) return <p className="dash-muted">No open tasks are assigned to you.</p>;
    return (
        <>
            <p className="dash-muted">{plural(total, 'open task')} assigned to you{total > work.data.length ? ` · showing ${work.data.length}` : ''}</p>
            <ul className="dash-list">
                {work.data.map(t => (
                    <li key={t.id} className="dash-attention">
                        <Link to={`/tasks/${t.id}`} className="dash-row-name">{t.title}</Link>
                        <span className="dash-muted">{t.project_name}{t.due_date ? ` · ${formatDueDate(t.due_date)}` : ''}</span>
                    </li>
                ))}
            </ul>
        </>
    );
}

/** Needs review: GET /me/reviews (viewer-scoped), as concept task rows. */
function Reviews({ reviews }: { reviews: WorkPage<ReviewItem> }) {
    const total = reviews.meta?.total ?? 0;
    if (total === 0) return <p className="dash-muted">No tasks you can see are waiting for review.</p>;
    return (
        <>
            <p className="dash-muted">{plural(total, 'task')} waiting for review{total > reviews.data.length ? ` · showing ${reviews.data.length}` : ''}</p>
            <ul className="dash-list dash-rows">
                {reviews.data.map(t => (
                    <li key={t.id} className="dash-task-row">
                        <span className="dash-status-dot dash-status-dot--review" aria-hidden="true" />
                        <span className="dash-task-copy">
                            <Link to={`/tasks/${t.id}`} className="dash-task-title">{t.title}</Link>
                            <span className="dash-task-sub">{t.project_name}{t.due_date ? ` · ${formatDueDate(t.due_date)}` : ''}</span>
                            <span className="dash-snippet">{t.handoff_summary || 'No handoff summary'}</span>
                        </span>
                        <span className="dash-pill dash-pill--purple">In review</span>
                    </li>
                ))}
            </ul>
        </>
    );
}

function Records({ entities }: { entities: Entity[] }) {
    if (entities.length === 0) {
        return <StateView state="empty" title="No records yet" action={<Link className="btn btn-secondary btn-md" to="/entities">Open Records</Link>} />;
    }
    const byType = new Map<string, { name: string; icon: string; color: string; count: number }>();
    for (const e of entities) {
        const t = byType.get(e.entity_type_id) ?? { name: e.entity_type_name, icon: e.entity_type_icon, color: e.entity_type_color, count: 0 };
        t.count++;
        byType.set(e.entity_type_id, t);
    }
    const recent = [...entities].sort((a, b) => b.created_at.localeCompare(a.created_at)).slice(0, 6);
    return (
        <>
            <p className="dash-muted">{plural(entities.length, 'record')}</p>
            <h3 className="dash-subhead" id="dash-rec-types">Records by type</h3>
            <ul className="dash-list" aria-labelledby="dash-rec-types">
                {[...byType.values()].sort((a, b) => b.count - a.count).map(t => (
                    <li key={t.name}>
                        <div className="dash-row-line"><span className="dash-row-name"><EntityIcon icon={t.icon} size={14} /> {t.name}</span><span className="dash-muted">{t.count}</span></div>
                        <div className="dash-bar"><div style={{ width: `${Math.max(4, (t.count / entities.length) * 100)}%`, background: t.color }} /></div>
                    </li>
                ))}
            </ul>
            <h3 className="dash-subhead" id="dash-rec-recent">Recent records</h3>
            <ul className="dash-list" aria-labelledby="dash-rec-recent">
                {recent.map(e => (
                    <li key={e.id} className="dash-attention">
                        <Link to={`/entities/${e.id}`} className="dash-row-name">
                            <span className="dash-icon" style={{ background: hexAlpha(e.entity_type_color || '#14867A', 0.13) }}><EntityIcon icon={e.entity_type_icon} size={14} /></span>
                            {e.name}
                        </Link>
                        <span className="dash-muted">{e.entity_type_name}</span>
                    </li>
                ))}
            </ul>
        </>
    );
}

function Heatmap({ cells }: { cells: { date: string; count: number }[] }) {
    const dayMap: Record<string, number> = {};
    for (const c of cells) dayMap[c.date] = (dayMap[c.date] || 0) + c.count;
    const max = Math.max(...Object.values(dayMap), 1);
    const total = Object.values(dayMap).reduce((s, n) => s + n, 0);
    const days: string[] = [];
    for (let i = HEAT_DAYS - 1; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        days.push(isoDay(d));
    }
    const weeks: string[][] = [];
    for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
    const color = (n: number) => {
        if (n === 0) return 'var(--color-surface-2)';
        const r = n / max;
        if (r < 0.25) return varAlpha('accent', 0.25);
        if (r < 0.5) return varAlpha('accent', 0.44);
        if (r < 0.75) return varAlpha('accent', 0.67);
        return 'var(--accent)';
    };
    return (
        <>
            <p className="dash-muted">{plural(total, 'task')} created in your projects · last 12 weeks</p>
            <div className="dash-heat" role="img" aria-label={`${total} tasks created in the last 12 weeks`}>
                {weeks.map((week, wi) => (
                    <div key={wi} className="dash-heat-week">
                        {week.map(date => <div key={date} title={`${date}: ${dayMap[date] || 0} created`} style={{ background: color(dayMap[date] || 0) }} />)}
                    </div>
                ))}
            </div>
            <div className="dash-heat-legend" aria-hidden="true">
                <span>Less</span>
                {['var(--color-surface-2)', varAlpha('accent', 0.25), varAlpha('accent', 0.44), varAlpha('accent', 0.67), 'var(--accent)'].map(c => (
                    <div key={c} style={{ background: c }} />
                ))}
                <span>More</span>
            </div>
        </>
    );
}

const SHORTCUTS = [
    { glyph: <BarChart3 size={22} />, label: 'Portfolio', desc: 'Progress and overdue work by project', to: PORTFOLIO_VIEW },
    { glyph: <LineChart size={22} />, label: 'Project Analytics', desc: 'Completion and status breakdown per project', to: '/projects', hint: 'Open a project, then Reports' },
    { glyph: <Flame size={22} />, label: 'Workload', desc: 'Assigned work by person', to: '/workload' },
    { glyph: <CalendarRange size={22} />, label: 'Timeline', desc: 'Task dates across projects', to: '/timeline' },
    { glyph: <Database size={22} />, label: 'Records', desc: 'Clients, assets and other records', to: '/entities' },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function DashboardPage() {
    usePageTitle('Dashboard');
    const [portfolio, retryPortfolio] = useResource(getPortfolio);
    const [work, retryWork] = useResource(loadMyWork);
    const [reviews, retryReviews] = useResource(loadReviews);
    const [entities, retryEntities] = useResource(getAllEntities);
    const [activity, retryActivity] = useResource(loadActivity);
    // Archived projects are left out of every portfolio card (and the Projects
    // count), the same way Deadlines leaves them out.
    const p = (lines: number, render: (d: PortfolioProject[]) => React.ReactNode) => (
        <Load res={portfolio} retry={retryPortfolio} what="projects" lines={lines}>
            {d => render(d.filter(x => x.status !== 'archived'))}
        </Load>
    );

    const more = (to: string, label: string) => <Link to={to} className="dash-inline">{label} <ArrowUpRight size={14} aria-hidden="true" /></Link>;

    return (
        <div className="dash">
            <PageHeader eyebrow="Workspace dashboard" title="The whole workspace. A clear view."
                subtitle="Track delivery, people, and decisions across the projects you can access." />

            {/* First-use checklist: renders only while a setup step is not done (U34). */}
            <OnboardingChecklist variant="dashboard" />

            <section aria-label="Summary">
                <Load res={portfolio} retry={retryPortfolio} what="projects" lines={2}>
                    {d => <Summary portfolio={d.filter(x => x.status !== 'archived')} reviews={reviews} />}
                </Load>
            </section>

            <section aria-labelledby="dash-projects" className="dash-projects">
                <div className="dash-section-label">
                    <h2 id="dash-projects" className="dash-panel-title">Projects you can access</h2>
                    {more('/projects', 'View projects')}
                </div>
                {p(3, d => <ProjectGrid portfolio={d} />)}
            </section>

            <div className="dash-columns">
                <div className="dash-stack">
                    <Panel id="dash-reviews" title="Needs review"
                        count={reviews.status === 'ready' ? reviews.data.meta?.total ?? 0 : undefined}
                        action={more('/reviews', 'Reviews')}>
                        <Load res={reviews} retry={retryReviews} what="reviews">{d => <Reviews reviews={d} />}</Load>
                    </Panel>
                    <Panel id="dash-work" title="Assigned to you" action={more('/my-work', 'My work')}>
                        <Load res={work} retry={retryWork} what="your assigned work">{d => <AssignedToYou work={d} />}</Load>
                    </Panel>
                </div>
                <div className="dash-stack">
                    <Panel id="dash-blocked" title="Blocked work"
                        count={portfolio.status === 'ready' ? portfolio.data.filter(x => x.status !== 'archived').reduce((n, x) => n + x.blocked_tasks, 0) : undefined}
                        action={more(PORTFOLIO_VIEW, 'Portfolio')}>
                        {p(3, d => <BlockedWork portfolio={d} />)}
                    </Panel>
                    <section className="card dash-panel dash-context" aria-labelledby="dash-next">
                        <span className="page-eyebrow">Your next step</span>
                        <h3 id="dash-next" className="dash-context-title">Work stays connected to the project.</h3>
                        <p className="dash-muted">Open a project for its overview, tasks, plan, activity, and access. Your agent works within the same project and task boundaries.</p>
                        <Link to="/connections" className="btn btn-secondary btn-sm dash-inline">Manage your agent connection <ArrowUpRight size={14} aria-hidden="true" /></Link>
                    </section>
                    <Panel id="dash-deadlines" title="Project deadlines" action={more('/timeline', 'Timeline')}>
                        {p(3, d => <Deadlines portfolio={d} />)}
                    </Panel>
                </div>
            </div>

            <div className="dash-columns">
                <Panel id="dash-activity" title="Activity">
                    <Load res={activity} retry={retryActivity} what="activity">{d => <Heatmap cells={d} />}</Load>
                </Panel>
                <Panel id="dash-records" title="Records" action={more('/entities', 'View all')}>
                    <Load res={entities} retry={retryEntities} what="records">{d => <Records entities={d} />}</Load>
                </Panel>
            </div>
            <RecentCommitsWidget />

            <section aria-labelledby="dash-tools">
                <h2 id="dash-tools" className="dash-panel-title dash-tools-title">Workload &amp; tools</h2>
                <div className="dash-shortcuts">
                    {SHORTCUTS.map(s => (
                        <Link key={s.label} to={s.to} className="card dash-shortcut">
                            <span className="dash-shortcut-icon">{s.glyph}</span>
                            <strong>{s.label}</strong>
                            <span className="dash-muted">{s.desc}</span>
                            {'hint' in s && <span className="dash-hint">{s.hint}</span>}
                        </Link>
                    ))}
                </div>
            </section>
            <p className="dash-muted dash-note"><ListChecks size={14} aria-hidden="true" /> Counts include only tasks you can see.</p>
        </div>
    );
}
